<template>
  <v-dialog v-model="dialog" persistent no-click-outside no-esc max-width="600px">
    <v-card class="elevation-12 rounded-lg">
      <v-card-title class="amber darken-2 white--text py-3 px-4 d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-clock-alert-outline</v-icon>
        <span class="text-h6 font-weight-bold">Justificativa de Atraso</span>
      </v-card-title>

      <v-card-text class="pt-4 pb-2 px-4">
        <v-alert type="warning" border="left" colored-border elevation="1" class="mb-4">
          <div class="subtitle-2 font-weight-bold grey--text text--darken-3">
            Foi identificado um registro de ponto em atraso no sistema.
          </div>
          <div class="caption grey--text text--darken-2 mt-1">
            Se desejar, informe a justificativa para este atraso.
          </div>
        </v-alert>

        <v-card v-if="atrasoPendente" outlined class="mb-4 grey lighten-5">
          <v-card-text class="py-3">
            <v-row dense>
              <v-col cols="6" sm="3">
                <div class="caption text-uppercase font-weight-bold grey--text">Data</div>
                <div class="subtitle-2 font-weight-bold primary--text">
                  {{ atrasoPendente.data_formatada || atrasoPendente.data }}
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="caption text-uppercase font-weight-bold grey--text">Previsto</div>
                <div class="subtitle-2 font-weight-bold">
                  {{ atrasoPendente.hora_prevista }}
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="caption text-uppercase font-weight-bold grey--text">Registrado</div>
                <div class="subtitle-2 font-weight-bold error--text">
                  {{ atrasoPendente.hora_registrada }}
                </div>
              </v-col>
              <v-col cols="6" sm="3">
                <div class="caption text-uppercase font-weight-bold grey--text">Tempo Atraso</div>
                <v-chip x-small color="warning" class="font-weight-bold white--text mt-1">
                  {{ atrasoPendente.tempo_atraso }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-form ref="form" v-model="valid" lazy-validation>
          <v-textarea
            v-model="form.motivo"
            label="Motivo da Justificativa *"
            placeholder="Descreva detalhadamente o motivo do atraso..."
            outlined
            dense
            rows="3"
            counter="500"
            :rules="[
              (v) => !!v || 'O motivo é obrigatório',
              (v) => (v && v.trim().length >= 5) || 'O motivo deve conter no mínimo 5 caracteres',
            ]"
            required
            class="mb-2"
          ></v-textarea>

          <v-file-input
            v-model="form.anexo"
            label="Anexo (Atestado, declaração, PDF ou imagem)"
            placeholder="Selecione um arquivo (opcional)"
            outlined
            dense
            prepend-icon="mdi-paperclip"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            show-size
            truncate-length="25"
            hint="Formatos permitidos: PDF, Imagens (PNG/JPG). Tamanho máx: 10MB."
            persistent-hint
          ></v-file-input>
        </v-form>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="py-3 px-4 bg-grey lighten-4">
        <v-spacer></v-spacer>
        <v-btn text class="mr-2" :disabled="loading" @click="cancelar">Cancelar</v-btn>
        <v-btn
          color="primary"
          class="font-weight-bold px-6"
          :loading="loading"
          :disabled="!valid || loading"
          @click="enviar"
        >
          <v-icon left>mdi-send</v-icon>
          Enviar Justificativa
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AtrasoJustificadoObligatorioModal",
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      atrasoPendente: null,
      form: {
        motivo: "",
        anexo: null,
      },
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    ...mapGetters("AppConfigurations", ["podeSolicitar"]),
    temPermissaoSolicitar() {
      // Desativado pela administração para o colaborador: o modal não abre.
      if (!this.podeSolicitar("atraso")) return false;
      if (this.user?.roleName === "super-admin") return true;
      return (
        Array.isArray(this.user?.permissions) &&
        this.user.permissions.includes("colaboradores.atrasosJustificados.solicitar")
      );
    },
  },
  mounted() {
    this.verificarPendencia();
  },
  methods: {
    async verificarPendencia() {
      if (!this.temPermissaoSolicitar) return false;

      try {
        const resp = await this.$services.colaboradorPontosService.verificarAtrasoPendente();
        if (resp && resp.pendente) {
          this.atrasoPendente = resp.pendente;
          this.dialog = true;
          return true;
        }
      } catch (error) {
        console.error("Erro ao verificar atrasos pendentes:", error);
      }
      return false;
    },
    async abrir() {
      const encontrou = await this.verificarPendencia();
      if (!encontrou && this.$toast) {
        this.$toast.info("Nenhum atraso pendente de justificativa.");
      }
    },
    async enviar() {
      if (!this.$refs.form.validate()) return;

      try {
        this.loading = true;

        const formData = new FormData();
        formData.append("colaborador_batida_id", this.atrasoPendente.colaborador_batida_id || "");
        formData.append("data", this.atrasoPendente.data);
        formData.append("hora_prevista", this.atrasoPendente.hora_prevista || "");
        formData.append("hora_registrada", this.atrasoPendente.hora_registrada || "");
        formData.append("minutos_atraso", this.atrasoPendente.minutos_atraso || 0);
        formData.append("motivo", this.form.motivo);

        if (this.form.anexo) {
          formData.append("anexo", this.form.anexo);
        }

        await this.$services.colaboradorPontosService.enviarAtrasoJustificado(formData);

        if (this.$toast) {
          this.$toast.success("Justificativa de atraso enviada com sucesso!");
        }

        this.dialog = false;
        this.atrasoPendente = null;
        this.form.motivo = "";
        this.form.anexo = null;

        // Emit check finished or refresh parent view
        this.$emit("enviado");
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.loading = false;
      }
    },
    cancelar() {
      this.dialog = false;
      this.form.motivo = "";
      this.form.anexo = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
  },
};
</script>

<style scoped>
.v-dialog {
  border-radius: 12px !important;
}
</style>
